import {
  Button,
  Checkbox,
  Combobox,
  type ComboboxOption,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FilterBlockEditor,
  Label,
} from "@tellescope/ui";
import { useTellescopeInfiniteQuery, useTellescopeQuery, useUsersQuery } from "@tellescope/tanstack-query";
import { getCalendarEventFieldOptions, type CalendarFilters } from "@tellescope/calendar";
import { Filter, Plus } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatName, type FreeformFilterBlock } from "@tellescope/utils";
import { Enduser, EnduserSearchCombobox } from "@tellescope/contacts";

export interface CalendarFilterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: CalendarFilters;
  onApply: (filters: CalendarFilters) => void;
  onClearAll?: () => void;
}

const generateBlockId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `block-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const makeEmptyBlock = (): FreeformFilterBlock => ({
  id: generateBlockId(),
  logic: "and",
  filters: [{ field: "", operation: "equals", value: undefined }],
});

export const CalendarFilterDialog = ({
  open,
  onOpenChange,
  filters,
  onApply,
  onClearAll,
}: CalendarFilterDialogProps) => {
  const { t } = useTranslation("calendar");
  const [staged, setStaged] = useState<CalendarFilters>(filters);
  const [wasOpen, setWasOpen] = useState(open);
  if (open && !wasOpen) {
    setWasOpen(true);
    setStaged(filters);
  } else if (!open && wasOpen) {
    setWasOpen(false);
  }

  const templatesQuery = useTellescopeInfiniteQuery({
    model: "calendar_event_templates",
    method: "getSome",
    request: { limit: 500 },
    select: (data) => data.pages.flat(),
  });
  const templateOptions = useMemo<ComboboxOption[]>(
    () =>
      (templatesQuery.data ?? []).map((tmpl) => ({
        value: tmpl.id,
        label: tmpl.displayTitle || tmpl.title,
        metadata: { durationInMinutes: tmpl.durationInMinutes },
      })),
    [templatesQuery.data]
  );
  const selectedTemplateOptions = useMemo<ComboboxOption[]>(
    () =>
      (staged.templateIds ?? []).map((id) => templateOptions.find((o) => o.value === id) ?? { value: id, label: id }),
    [staged.templateIds, templateOptions]
  );

  const locationsQuery = useTellescopeInfiniteQuery({
    model: "appointment_locations",
    method: "getSome",
    request: { limit: 500 },
    select: (data) => data.pages.flat(),
  });
  const locationOptions = useMemo<ComboboxOption[]>(
    () =>
      (locationsQuery.data ?? []).map((loc) => ({
        value: loc.id,
        label: loc.title,
        metadata: { description: loc.address || loc.city || loc.state || t("Virtual") },
      })),
    [locationsQuery.data, t]
  );
  const selectedLocationOptions = useMemo<ComboboxOption[]>(
    () =>
      (staged.locationIds ?? []).map((id) => locationOptions.find((o) => o.value === id) ?? { value: id, label: id }),
    [staged.locationIds, locationOptions]
  );

  const { data: users = [] } = useUsersQuery();
  const userOptions = useMemo<ComboboxOption[]>(
    () =>
      users.reduce<ComboboxOption[]>((options, user) => {
        if (user.hideFromCalendarView) return options;
        options.push({ value: user.id, label: formatName(user) || user.id });
        return options;
      }, []),
    [users]
  );
  const selectedUserOptions = useMemo<ComboboxOption[]>(
    () => (staged.userIds ?? []).map((id) => userOptions.find((o) => o.value === id) ?? { value: id, label: id }),
    [staged.userIds, userOptions]
  );

  const { data: queriedEndusers } = useTellescopeQuery({
    model: "endusers",
    method: "getByIds",
    request: { ids: staged.enduserIds ?? [] },
    enabled: !!staged.enduserIds?.length,
    select: (data) => data.matches,
  });

  const setField = useCallback(<K extends keyof CalendarFilters>(key: K, value: CalendarFilters[K]) => {
    setStaged((prev) => ({ ...prev, [key]: value }));
  }, []);

  const updateBlock = useCallback((index: number, next: FreeformFilterBlock) => {
    setStaged((prev) => ({
      ...prev,
      customBlocks: (prev.customBlocks ?? []).map((block, i) => (i === index ? next : block)),
    }));
  }, []);

  const removeBlock = useCallback((index: number) => {
    setStaged((prev) => ({
      ...prev,
      customBlocks: (prev.customBlocks ?? []).filter((_, i) => i !== index),
    }));
  }, []);

  const addBlock = useCallback(() => {
    setStaged((prev) => ({
      ...prev,
      customBlocks: [...(prev.customBlocks ?? []), makeEmptyBlock()],
    }));
  }, []);

  const handleApply = () => {
    onApply(staged);
    onOpenChange(false);
  };

  const handleClearAll = () => {
    setStaged({});
    if (onClearAll) onClearAll();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="calendar-filter-dialog"
        className="max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden"
        divided
      >
        <DialogHeader>
          <DialogTitle>{t("Calendar Filters")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 p-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <span className="text-sm font-medium">{t("Templates")}</span>
              <Combobox
                value={selectedTemplateOptions}
                onChange={(selected) => {
                  const ids = Array.isArray(selected) ? selected.map((s) => s.value) : [];
                  setField("templateIds", ids.length ? ids : undefined);
                }}
                options={templateOptions}
                multiple
                placeholder={t("Select templates")}
                searchPlaceholder={t("Search templates...")}
                formatSelectedValue={({ length }) => t("{{amount}} selected", { amount: length })}
                formatOption={(option) => (
                  <div
                    key={option.option.value}
                    className="flex items-center justify-between w-full cursor-pointer"
                    role="button"
                    onClickCapture={(e) => {
                      const isSelected = staged.templateIds?.includes(option.option.value);
                      e.stopPropagation();
                      e.preventDefault();
                      setField(
                        "templateIds",
                        isSelected
                          ? staged.templateIds?.filter((id) => id !== option.option.value) ?? []
                          : [...(staged.templateIds ?? []), option.option.value]
                      );
                    }}
                  >
                    <span className="flex items-center gap-2 flex-grow min-w-0">
                      <Checkbox checked={option.isSelected} id={option.option.value} className="flex-shrink-0" />
                      <Label
                        htmlFor={option.option.value}
                        className="font-light pointer-events-none block line-clamp-2 min-w-0"
                      >
                        {option.option.label}
                      </Label>
                    </span>
                    <span className="text-muted-foreground text-xs text-nowrap flex-shrink-0">
                      {t("{{duration}} min", { duration: option.option.metadata?.durationInMinutes })}
                    </span>
                  </div>
                )}
                popoverClassName="w-[300px]"
                popoverAlign="end"
              />
            </div>
            <div className="space-y-1.5 flex-grow">
              <span className="text-sm font-medium">{t("Locations")}</span>
              <Combobox
                value={selectedLocationOptions}
                onChange={(selected) => {
                  const ids = Array.isArray(selected) ? selected.map((s) => s.value) : [];
                  setField("locationIds", ids.length ? ids : undefined);
                }}
                options={locationOptions}
                multiple
                placeholder={t("Select locations")}
                searchPlaceholder={t("Search locations...")}
                formatSelectedValue={({ length }) => t("{{amount}} selected", { amount: length })}
                formatOption={(option) => (
                  <div
                    key={option.option.value}
                    className="flex items-center justify-between w-full cursor-pointer"
                    role="button"
                    onClickCapture={(e) => {
                      const isSelected = staged.locationIds?.includes(option.option.value);
                      e.stopPropagation();
                      e.preventDefault();
                      setField(
                        "locationIds",
                        isSelected
                          ? staged.locationIds?.filter((id) => id !== option.option.value) ?? []
                          : [...(staged.locationIds ?? []), option.option.value]
                      );
                    }}
                  >
                    <span className="flex items-center gap-2">
                      <Checkbox checked={option.isSelected} id={option.option.value} />
                      <Label
                        htmlFor={option.option.value}
                        className="font-light pointer-events-none line-clamp-2 block"
                      >
                        {option.option.label}
                      </Label>
                    </span>
                    {!!option.option.metadata?.description && (
                      <span className="text-muted-foreground text-xs text-nowrap flex-shrink-0">
                        {String(option.option.metadata?.description)}
                      </span>
                    )}
                  </div>
                )}
                popoverClassName="w-[300px]"
                popoverAlign="start"
              />
            </div>
          </div>
          <div className="space-y-4">
            <span className="text-md font-medium">{t("Status Filters")}</span>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  data-testid="calendar-filter-hide-cancelled"
                  checked={!!staged.hideCancelled}
                  onCheckedChange={(checked) => setField("hideCancelled", checked === true ? true : undefined)}
                />
                {t("Hide Cancelled Events")}
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  data-testid="calendar-filter-hide-no-show"
                  checked={!!staged.hideNoShow}
                  onCheckedChange={(checked) => setField("hideNoShow", checked === true ? true : undefined)}
                />
                {t("Hide No-showed Events")}
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  data-testid="calendar-filter-hide-rescheduled"
                  checked={!!staged.hideRescheduled}
                  onCheckedChange={(checked) => setField("hideRescheduled", checked === true ? true : undefined)}
                />
                {t("Hide Rescheduled Events")}
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  data-testid="calendar-filter-hide-events-i-created-but-am-not-attending"
                  checked={!!staged.hideEventsICreatedButAmNotAttending}
                  onCheckedChange={(checked) =>
                    setField("hideEventsICreatedButAmNotAttending", checked === true ? true : undefined)
                  }
                />
                {t("Hide Events I Created But Am Not Attending")}
              </label>
            </div>
          </div>
          <div className="space-y-1.5">
            <span className="text-sm font-medium">{t("Users")}</span>
            <Combobox
              value={selectedUserOptions}
              onChange={(selected) => {
                const ids = Array.isArray(selected) ? selected.map((s) => s.value) : [];
                setField("userIds", ids.length ? ids : undefined);
              }}
              options={userOptions}
              multiple
              placeholder={t("Select users")}
              searchPlaceholder={t("Search users...")}
              formatSelectedValue={({ length }) => t("{{amount}} selected", { amount: length })}
            />
          </div>

          <div className="space-y-1.5">
            <span className="text-sm font-medium">{t("Patients")}</span>
            <EnduserSearchCombobox
              value={queriedEndusers ?? []}
              onChange={(endusers: Enduser[]) =>
                setField("enduserIds", endusers.length ? endusers.map((e) => e.id) : undefined)
              }
              multiple
              placeholder={t("Select patients")}
              onlyDisplayCount
              showAsBadges
              clearable={false}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-md font-medium">{t("Custom Conditions")}</span>
              <Button
                variant="outline"
                size="sm"
                className="rounded-md font-light text-xs p-1 h-auto"
                onClick={addBlock}
              >
                <Plus className="h-2 w-2" />
                {t("Add Condition Block")}
              </Button>
            </div>
            <div className="flex flex-col items-stretch gap-2 w-full">
              {staged.customBlocks?.length ? (
                staged.customBlocks.map((block, index) => (
                  <div key={block.id} className="flex flex-col gap-4 items-stretch w-full">
                    {index !== 0 && (
                      <div className="px-2 py-1 rounded-full bg-muted text-muted-foreground text-xs w-min self-center">
                        {t("AND")}
                      </div>
                    )}
                    <FilterBlockEditor
                      className="w-full"
                      block={block}
                      fieldOptions={getCalendarEventFieldOptions()}
                      onChange={(next) => updateBlock(index, next)}
                      onRemove={() => removeBlock(index)}
                    />
                  </div>
                ))
              ) : (
                <div className="rounded-sm border-dashed border-2 flex flex-col items-center py-10 text-muted-foreground text-sm font-light gap-2 w-full">
                  <Filter size={36} className="text-gray-400" />
                  <div className="flex flex-col items-center gap-0.5">
                    <span>{t("No custom conditions added")}</span>
                    <span className="text-xs">{t('Click "Add Condition Block" to create complex filters')}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <DialogFooter className="flex flex-row items-center justify-between sm:justify-between gap-2 bg-muted">
          <Button
            data-testid="calendar-filter-clear-all"
            variant="ghost"
            onClick={handleClearAll}
            className="font-light hover:bg-gray-200"
          >
            {t("Clear all")}
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="font-light">
              {t("Cancel")}
            </Button>
            <Button data-testid="calendar-filter-apply" onClick={handleApply} className="font-light">
              {t("Apply Filters")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
